// Top-level route table (sub-plan 13 §13.4 + sub-plan 14 §all).
// UNAUTH routes: /login, /forgot-password, /reset (magic-link landing — must
// be reachable without a session).
// Protected: everything under /app/* via ProtectedLayout (sidebar shell).

import { Routes, Route, Navigate } from "react-router-dom";
import { SignIn } from "@/pages/auth/SignIn";
import { ForgotPassword } from "@/pages/auth/ForgotPassword";
import { ResetPasswordByToken } from "@/pages/auth/ResetPasswordByToken";
import { ResetPasswordWithCode } from "@/pages/auth/ResetPasswordWithCode";
import { ProtectedLayout } from "@/pages/auth/ProtectedLayout";
import { Dashboard } from "@/pages/dashboard/Dashboard";
import { AdminUserList } from "@/pages/admin-users/AdminUserList";
import { AdminUserEdit } from "@/pages/admin-users/AdminUserEdit";
import { AdminUserNew } from "@/pages/admin-users/AdminUserNew";
import { RoleList } from "@/pages/admin-users/RoleList";
import { RoleEdit } from "@/pages/admin-users/RoleEdit";
import { MyProfile } from "@/pages/profile/MyProfile";
import { AppUserList } from "@/pages/app-users/AppUserList";
import { AppUserEdit } from "@/pages/app-users/AppUserEdit";
import { AppUserNew } from "@/pages/app-users/AppUserNew";
import { AppUserResetPassword } from "@/pages/app-users/AppUserResetPassword";
import { ApprovalQueue } from "@/pages/app-users/ApprovalQueue";
import { ApprovalDetail } from "@/pages/app-users/ApprovalDetail";
import { JobList } from "@/pages/jobs/JobList";
import { JobForm } from "@/pages/jobs/JobForm";
import { DisputeList } from "@/pages/disputes/DisputeList";
import { TicketsList } from "@/pages/tickets/TicketsList";
import { PushNotifications } from "@/pages/notifications/PushNotifications";
import { EmailNotifications } from "@/pages/notifications/EmailNotifications";
import { CommissionReport } from "@/pages/reports/CommissionReport";
import { BillingReport } from "@/pages/reports/BillingReport";
import { WithdrawalsList } from "@/pages/reports/WithdrawalsList";
import { JobCategoriesList } from "@/pages/config/JobCategoriesList";
import { FaqTemplatesList } from "@/pages/config/FaqTemplatesList";
import { PriceTiersList } from "@/pages/config/PriceTiersList";
import { RewardManagement } from "@/pages/rewards/RewardManagement";

export function App() {
  return (
    <Routes>
      {/* UNAUTH */}
      <Route path="/login" element={<SignIn />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-with-code" element={<ResetPasswordWithCode />} />
      <Route path="/reset" element={<ResetPasswordByToken />} />

      {/* PROTECTED — sub-plan 14 modules */}
      <Route path="/app" element={<ProtectedLayout />}>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="admin-users" element={<AdminUserList />} />
        <Route path="admin-users/new" element={<AdminUserNew />} />
        <Route path="admin-users/roles" element={<RoleList />} />
        <Route path="admin-users/roles/:id" element={<RoleEdit />} />
        <Route path="admin-users/me" element={<AdminUserEditMeRedirect />} />
        <Route path="admin-users/:id" element={<AdminUserEdit />} />
        <Route path="app-users" element={<AppUserList />} />
        <Route path="app-users/new" element={<AppUserNew />} />
        <Route path="app-users/:id/reset" element={<AppUserResetPassword />} />
        <Route path="app-users/approval" element={<ApprovalQueue />} />
        <Route path="app-users/approval/:id" element={<ApprovalDetail />} />
        {/* Catch-all UUID-shaped param — MUST be after /new, /reset, etc.
            so the literal segments win. */}
        <Route path="app-users/:id" element={<AppUserEdit />} />
        <Route path="jobs" element={<JobList />} />
        <Route path="jobs/add" element={<JobForm />} />
        <Route path="jobs/:id" element={<JobForm />} />
        <Route path="disputes" element={<DisputeList />} />
        <Route path="tickets" element={<TicketsList />} />
        <Route path="notifications/push" element={<PushNotifications />} />
        <Route path="notifications/email" element={<EmailNotifications />} />
        <Route path="reports/commission" element={<CommissionReport />} />
        <Route path="reports/billing" element={<BillingReport />} />
        <Route path="reports/withdrawals" element={<WithdrawalsList />} />
        <Route path="config/job-categories" element={<JobCategoriesList />} />
        <Route path="config/price-tiers" element={<PriceTiersList />} />
        <Route path="config/faq" element={<FaqTemplatesList />} />
        <Route path="rewards" element={<RewardManagement />} />
        <Route path="profile" element={<MyProfile />} />
      </Route>

      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

// Helper: /app/admin-users/me redirects to /app/admin-users/<callerId> so the
// edit form can hit GET /admin-users/:id with the correct UUID.
import { Navigate as Redirect } from "react-router-dom";
import { useAdminMe } from "@/hooks/useAdminMe";
function AdminUserEditMeRedirect() {
  const { data, isLoading } = useAdminMe();
  if (isLoading) return null;
  const uid = data?.profile?.id;
  if (!uid) return <Redirect to="/app/dashboard" replace />;
  return <Redirect to={`/app/admin-users/${uid}`} replace />;
}
